import type { ApiResponse, CreateUserPayload, PaginatedResponse, UpdateUserPayload } from '@klaro7/shared-types';

import api from '@/lib/api';

export interface AdminUser {
  id: string;
  email: string;
  role: 'super_admin' | 'admin' | 'professional' | 'user';
  status: 'active' | 'pending' | 'waiting_questionnaire' | 'suspended' | 'inactive';
  profile: {
    firstName: string;
    lastName: string;
    avatar?: string;
    emailVerified?: boolean;
    emailVerifiedAt?: string;
    phone?: string;
    phoneVerified?: boolean;
    phoneVerifiedAt?: string;
    pendingPhone?: string;
    bio?: string;
  };
  createdAt: string;
  updatedAt: string;
  /** Stato segnalazione antifrode (assente = mai flaggato). */
  fraudStatus?: 'flagged' | 'confirmed' | 'cleared';
  /** Audit minimo dei cambi di stato: chi e quando. */
  statusHistory?: Array<{ status: string; at: string; byAdminId: string; byAdminName: string }>;
  /** Read-only wallet snapshot; null if the user has no wallet yet. */
  wallet?: {
    balance: number;
    bonusBalance: number;
    totalBalance: number;
    currency: string;
  } | null;
}

export interface AdminUserQueryParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  role?: string;
  status?: string;
  email?: string;
  q?: string;
  /** Filtro segnalazioni antifrode. */
  fraud?: 'flagged' | 'confirmed' | 'cleared' | 'any';
}

export interface UserStats {
  users: number;
  activeUsers: number;
  professionals: number;
}

/** Real platform user counts (from the User collection) for the admin home. */
export async function getUserStats(): Promise<UserStats> {
  const response = await api.get<ApiResponse<UserStats>>('/users/stats');
  return response.data.data;
}

export async function getAdminUsers(
  params?: AdminUserQueryParams
): Promise<PaginatedResponse<AdminUser>> {
  const response = await api.get<PaginatedResponse<AdminUser>>('/users', { params });
  return response.data;
}

export async function getAdminUserById(id: string): Promise<AdminUser> {
  const response = await api.get<ApiResponse<AdminUser>>(`/users/${id}`);
  return response.data.data;
}

export type CreateAdminUserInput = CreateUserPayload;

export async function createAdminUser(data: CreateAdminUserInput): Promise<AdminUser> {
  const response = await api.post<ApiResponse<AdminUser>>('/users', data);
  return response.data.data;
}

export type UpdateAdminUserInput = UpdateUserPayload;

/** Update a user (status, role, profile, …). */
export async function updateUser(id: string, payload: UpdateAdminUserInput): Promise<AdminUser> {
  const response = await api.put<ApiResponse<AdminUser>>(`/users/${id}`, payload);
  return response.data.data;
}

/** Permanently delete a user. */
export async function deleteUser(id: string): Promise<void> {
  await api.delete(`/users/${id}`);
}

/** Super-admin only: force a new password for any user. */
export async function forceUserPassword(id: string, password: string): Promise<void> {
  await api.put(`/users/${id}/password`, { password });
}

export interface GrantGiftCreditPayload {
  amount: number;
  reason: string;
}

/** Super-admin only: grant a no-expiry gift credit to a user's bonus wallet. */
export async function grantGiftCredit(userId: string, data: GrantGiftCreditPayload): Promise<void> {
  await api.post(`/users/${userId}/grant-bonus`, data);
}

export interface WalletTx {
  _id: string;
  type: string;
  amount: number;
  balanceAfter: number;
  description: string;
  createdAt: string;
}

export interface WalletTxPage {
  items: WalletTx[];
  pagination: {
    page: number; limit: number; total: number; totalPages: number;
    hasNextPage: boolean; hasPrevPage: boolean;
  };
}

export async function getUserWalletTransactions(userId: string, page: number): Promise<WalletTxPage> {
  const res = await api.get(`/users/${userId}/wallet-transactions`, { params: { page, limit: 10 } });
  // sendPaginated → { success, data: [...], pagination: {...} }
  return { items: res.data.data as WalletTx[], pagination: res.data.pagination };
}
